import Two from 'two.js';
import { Line } from 'two.js/src/shapes/line';
import { Anchor } from 'two.js/src/anchor';
import arm from '../../assets/images/arm.png';
import trash from '../../assets/images/trash-bin.svg';

export const commonStartAngle = (Math.PI * 2) * (3 / 4);

export const drawFpsCounter = (two: Two) => {
  const text = new Two.Text(
    '- fps',
    two.width-10,
    two.height-10
  );
  text.fill = '#FFFFFF';
  text.size = 20;
  text.alignment = 'right';
  two.add(text);
  return text;
};

function createArcPoints(
  cx: number,
  cy: number,
  r: number,
  startAngle: number,
  endAngle: number,
  segments: number,
): Anchor[] {
  const points: Anchor[] = [];
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const angle = startAngle + t * (endAngle - startAngle);
    points.push(
      new Two.Anchor(
        cx + r * Math.cos(angle),
        cy + r * Math.sin(angle),
      ),
    );
  }
  return points;
}

export const drawHand = (two: Two) => {
  const texture = new Two.Texture(arm);

  const spriteRect = two.makeRectangle(0, 0, 2048, 2048);
  spriteRect.fill = texture;
  spriteRect.noStroke();
  spriteRect.scale = 0.2;
  return spriteRect;
};

export const drawTrash = (two: Two) => {
  const texture = new Two.Texture(trash);
  texture.repeat = 'no-repeat';

  const spriteRect = two.makeRectangle(0, 0, 512, 512);
  spriteRect.fill = texture;
  spriteRect.noStroke();
  spriteRect.scale = 1;
  return spriteRect;
};

export const drawTicker = (two: Two) => {
  const scale = 12;

  const arcCornerOffset = Math.PI * 0.15;
  const arcPoints = createArcPoints(0, 0, scale, Math.PI - arcCornerOffset,
    Math.PI * 2 + arcCornerOffset, 20); // 0 to π (semi-circle)
  const points = [...arcPoints, new Two.Anchor(0, scale * 2.5)];

  const ticker = two.makePath(points);
  const pin = two.makeCircle(0, 0, scale * 0.3)
    .noStroke();
  const tickerGroup = two.makeGroup(ticker, pin);

  ticker.stroke = '#fff';
  ticker.linewidth = 2;
  ticker.fill = '#000207';

  return tickerGroup;
};

export const createOuterBorder = (parameters: { wheelRadius: number }, two: Two) => {
  const circle = two.makeCircle(0, 0, parameters.wheelRadius);
  circle.fill = 'rgba(255, 0, 0, 0)';
  circle.stroke = '#fff';
  circle.linewidth = 4;
  return circle;
};

type HasTickets = { tickets: string | number };

export const getNumTickets = (players: HasTickets[]) => players
  .map((p) => parseInt(String(p.tickets), 10))
  .reduce((a, b) => a + b, 0);

export const accumulatedTicketSum = (array: HasTickets[]): number[] => array.reduce((acc: number[], current, index) => {
  const prev = acc.length ? acc[index - 1] : 0;
  acc.push(prev + Number(current.tickets));
  return acc;
}, []);

export const accumulatedAngleSum = (array: Array<string | number>): number[] => array.reduce((acc: number[], current, index) => {
  const prev = acc.length ? acc[index - 1] : commonStartAngle;
  acc.push(prev + Number(current));
  return acc;
}, []);

export const updateTicketEdgeLine = (ticketEdgeLine: Line, newAngle: number, parameters: { wheelRadius: number }) => {
  const x = Math.cos(newAngle) * (parameters.wheelRadius);
  const y = Math.sin(newAngle) * (parameters.wheelRadius);
  ticketEdgeLine.translation.set(0, 0);
  ticketEdgeLine.vertices[0].set(0, 0);
  ticketEdgeLine.vertices[1].set(x, y);
};

export const createTicketEdgeLine = (angle: number, centerOpacity: number, parameters: { wheelRadius: number }, two: Two) => {
  const x = Math.cos(angle) * (parameters.wheelRadius);
  const y = Math.sin(angle) * (parameters.wheelRadius);

  const gradient = new Two.LinearGradient(
    0, 0,
    x, y,
    [
      new Two.Stop(0, 'rgb(255, 255, 255)', centerOpacity),
      new Two.Stop(0.1, 'rgb(255, 255, 255)', centerOpacity),
      new Two.Stop(0.9, 'rgb(255, 255, 255)', 1),
      new Two.Stop(1, 'rgb(255, 255, 255)', 1),
    ]
  );

  gradient.units = 'userSpaceOnUse';
  const line = two.makeLine(0, 0, x, y);
  line.stroke = gradient;
  line.linewidth = 3;
  return line;
};

export const createWedgeBackground = (startAngle: number, endAngle: number, color: string, parameters: { wheelRadius: number }, two: Two) => {
  const arc = two.makeArcSegment(
    0,
    0,
    0,
    parameters.wheelRadius,
    startAngle,
    endAngle,
  );
  arc.fill = color;
  arc.stroke = 'none';
  return arc;
};

export const createWedgeText = (startTextAngle: number, endTextAngle: number, name: string, parameters: { wheelRadius: number; textPos: number }) => {
  const textAngle = startTextAngle + (endTextAngle - startTextAngle) / 2.0;
  const nameToRender = name.slice(0, 16);
  const text = new Two.Text(
    nameToRender,
    Math.cos(textAngle) * (parameters.wheelRadius * parameters.textPos),
    Math.sin(textAngle) * (parameters.wheelRadius * parameters.textPos)
  );
  const wedgeSizeTextFactor = Math.min(1, (endTextAngle - startTextAngle) / (Math.PI * 0.075));
  const nameLengthSizeFactor = Math.min(1, (1 - (nameToRender.length - 10) * 0.08));
  text.rotation = textAngle;
  text.fill = '#FFFFFF';
  text.alignment = 'right';
  text.size = parameters.wheelRadius * 0.13 * wedgeSizeTextFactor * nameLengthSizeFactor;
  text.family = 'Roboto';
  return text;
};

export const createDiscoLight = (two: Two) => {
  const colors = [
    'rgb(78,187,246)',
    'rgb(239,37,37)',
    'rgb(255,218,65)',
    'rgb(78,246,81)',
    'rgb(78,187,246)',
    'rgb(239,37,37)',
    'rgb(255,218,65)',
    'rgb(78,246,81)',
    'rgb(78,187,246)',
    'rgb(239,37,37)',
    'rgb(255,218,65)',
    'rgb(78,246,81)',
    'rgb(78,187,246)',
    'rgb(239,37,37)',
    'rgb(255,218,65)',
    'rgb(78,246,81)',
    'rgb(78,187,246)',

  ];
  const segmentSize = (Math.PI * 2) / (colors.length);
  const group = two.makeGroup();
  colors.forEach((color, index) => {
    const start = index * segmentSize;
    const end = (index + 0.5) * segmentSize;
    const light = two.makeArcSegment(0, 0, 100, 1400, start, end);
    light.opacity = 0.3;
    light.fill = color;
    light.stroke = 'none';
    group.add(light);
  });

  return group;
};

export const getCoordinatesInCircle = (radius: number, angle: number) => ({
  x: Math.cos(angle) * (radius),
  y: Math.sin(angle) * (radius)
});

export const getRandomCoordinatesInCircle = (radius: number) => {
  const angle = Math.random() * 2 * Math.PI;
  const r = Math.sqrt(Math.random()) * radius; // √ for uniform distribution
  return {
    x: Math.cos(angle) * r,
    y: Math.sin(angle) * r
  };
};
